import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const initHeader = () => {
  // Header section show images on load
  // [data-item]="{playIndex}"
  const container = document.querySelector('.js-section-header');
  const items = container?.querySelectorAll('[data-item]') || [];
  const sortedItems = Array.from(items).sort((a, b) => {
    const aIndex = parseInt(a.getAttribute('data-item'), 10) || 0;
    const bIndex = parseInt(b.getAttribute('data-item'), 10) || 0;
    return aIndex - bIndex;
  });
  window.addEventListener('curtain', () => {
    gsap.fromTo(
      sortedItems,
      {
        opacity: 0,
        scale: 0,
      },
      {
        duration: 1,
        opacity: 1,
        scale: 1,
        ease: 'power2.out',
        stagger: {
          each: 0.1,
          amount: 1,
        },
      },
    );
  });
};

const initProduct = () => {
  // Product section navigation
  // [data-nav]="{navIndex}"
  // [data-slide]="{slideIndex}"
  const container = document.querySelector('.js-section-products');
  const navs = container?.querySelectorAll('[data-nav]') || [];
  const slides = container?.querySelectorAll('[data-slide]') || [];
  if (!container || navs.length === 0 || slides.length === 0) {
    return;
  }

  const scrollToLabel = (timeline, label) => {
    const { scrollTrigger } = timeline;
    const scrollY =
      scrollTrigger.start +
      (scrollTrigger.end - scrollTrigger.start) * (timeline.labels[label] / timeline.duration());
    window.scrollTo({ top: scrollY, behavior: 'smooth' });
  };

  const handleNavClicked = (timeline, item, index) => {
    const href = item.querySelector('a');
    href.addEventListener('click', e => {
      e.preventDefault();
      scrollToLabel(timeline, index);
    });
  };

  ScrollTrigger.matchMedia({
    '(min-width: 720px)': () => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: 'top top',
          end: '+=1500',
          pin: true,
          anticipatePin: 1,
          scrub: 1,
          snap: {
            snapTo: 'labels',
            duration: 0.3,
            delay: 0,
            ease: 'none',
          },
          invalidateOnRefresh: true,
        },
      });
      timeline.to({}, { duration: 1 });
      navs.forEach((item, index) => {
        handleNavClicked(timeline, item, index);
        // Initial state, show the first item
        if (index === 0) {
          timeline.addLabel('0');
          return;
        }
        timeline
          // Hide previous
          .fromTo(
            navs[index - 1],
            {
              opacity: 1,
            },
            {
              opacity: 0.3,
              duration: 1,
            },
          )
          .fromTo(
            slides[index - 1],
            {
              opacity: 1,
              y: 0,
            },
            {
              opacity: 0,
              y: -100,
              duration: 1,
            },
            '<',
          )

          // Show current
          .fromTo(
            navs[index],
            {
              opacity: 0.3,
            },
            {
              opacity: 1,
              duration: 1,
            },
          )
          .fromTo(
            slides[index],
            {
              opacity: 0,
              y: 100,
            },
            {
              opacity: 1,
              y: 0,
              duration: 1,
            },
            '<',
          )
          .addLabel(`${index}`);
      });
      timeline.to({}, { duration: 1 });
    },
  });
};

const initCheckout = () => {
  // Checkout section navigation
  const container = document.querySelector('.js-section-checkout');
  const content = document.querySelector('.js-section-checkout-content');
  const slider = document.querySelector('.js-section-checkout-slider');
  const slides = slider?.querySelectorAll('[data-slide]') || [];
  if (!container || !content || !slider || slides.length === 0) {
    return;
  }
  ScrollTrigger.matchMedia({
    '(min-width: 720px)': () => {
      const scrollTrigger = {
        trigger: container,
        start: 'top top',
        end: () => `${slider.offsetWidth}`,
        pin: true,
        anticipatePin: 1,
        scrub: 1,
        snap: {
          snapTo: 1 / (slides.length - 1),
          duration: 0.3,
          delay: 0.3,
          ease: 'none',
        },
        invalidateOnRefresh: true,
        preventOverlaps: true,
      };
      ScrollTrigger.matchMedia({
        '(min-width: 720px) and (min-height: 1120px)': () => {
          gsap.to(slider, {
            x: () => `${-(slider.offsetWidth / (slides.length / 2) + 20)}px`, // 20px margin
            ease: 'none',
            scrollTrigger: {
              ...scrollTrigger,
              trigger: container,
            },
          });
        },
        '(min-width: 720px) and (max-height: 1119px)': () => {
          gsap.to(slider, {
            x: () => `${-(slider.offsetWidth / (slides.length / 2) + 20)}px`, // 20px margin
            ease: 'none',
            scrollTrigger: {
              ...scrollTrigger,
              trigger: content,
            },
          });
        },
      });
    },
  });
};

const initWorkflow = () => {
  const container = document.querySelector('.js-section-workflows');
  const pinItem = container?.querySelector('[data-pin]');
  if (!container || !pinItem) {
    return;
  }
  ScrollTrigger.matchMedia({
    '(min-width: 1024px) and (max-height: 1214px)': () => {
      ScrollTrigger.create({
        trigger: container,
        pin: pinItem,
        anticipatePin: 1,
        start: 'top top',
        end: 'bottom bottom',
        invalidateOnRefresh: true,
      });
    },
  });
};

const initStatisticNumber = () => {
  const number = document.querySelector('.js-section-statistic-number');
  if (!number) {
    return;
  }
  const from = { value: number.getAttribute('value') || '1500' };
  const target = number.getAttribute('target');
  const split = target.split('.');
  const decimals = split.length > 1 ? split[1].length : 0;
  gsap.to(from, {
    value: target,
    duration: 1.5,
    scrollTrigger: {
      trigger: number,
      start: 'bottom bottom',
      invalidateOnRefresh: true,
    },
    onUpdate: () => {
      const value = parseFloat(from.value);
      if (value) {
        number.setAttribute('value', value.toFixed(decimals));
      }
    },
  });
};

(() => {
  initHeader();
  initProduct();
  initCheckout();
  initWorkflow();
  initStatisticNumber();
})();
